/**
 * PIN gate context — centralizes the "try action → 423 → prompt PIN →
 * retry" flow so individual components can call `withPin(action)` and
 * not worry about the details.
 */
import { createContext, useCallback, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError } from '../api';
import { PinPromptModal } from './PinPromptModal';

type PendingAction = () => Promise<unknown> | void;

interface PinGateApi {
  /**
   * Run `action`. If it throws a 423 PinRequired, show the PIN modal,
   * and on successful unlock, re-run the action.
   */
  withPin: (action: PendingAction) => Promise<void>;
}

const Ctx = createContext<PinGateApi | null>(null);

export function PinGateProvider({ children }: { children: ReactNode }) {
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  const withPin: PinGateApi['withPin'] = useCallback(
    async (action) => {
      try {
        await action();
      } catch (err) {
        if (err instanceof ApiError && err.status === 423) {
          // Queue the action; modal opens because pendingAction is truthy.
          setPendingAction(() => action);
          return;
        }
        throw err;
      }
    },
    [],
  );

  return (
    <Ctx.Provider value={{ withPin }}>
      {children}
      <PinPromptModal
        open={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        onUnlocked={async () => {
          const a = pendingAction;
          setPendingAction(null);
          if (a) {
            try {
              await a();
            } catch {
              // swallow; action's own error handler should have dealt with it
            }
          }
        }}
      />
    </Ctx.Provider>
  );
}

export function usePinGate(): PinGateApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('usePinGate called outside PinGateProvider');
  return v;
}
